import { useState } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { challengesAPI } from '../../services/api'
import {
  TrophyIcon,
  PlusIcon,
  TrashIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline'
import { toast } from 'react-hot-toast'
import { format } from 'date-fns'

export default function CreateChallenge() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    difficulty: 'beginner',
    startDate: '',
    endDate: '',
    maxTeamSize: 1,
    prizes: [''],
    rules: [''],
    evaluationCriteria: ['']
  })

  const [errors, setErrors] = useState({})

  const createMutation = useMutation(
    (challengeData) => challengesAPI.create(challengeData),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries('admin-challenges')
        toast.success('Challenge created successfully!')
        navigate('/admin/challenges')
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.message || 'Failed to create challenge'
        toast.error(errorMessage)
        
        // Handle validation errors
        if (error.response?.data?.errors) {
          const validationErrors = {}
          error.response.data.errors.forEach(err => {
            validationErrors[err.path || err.param] = err.msg
          })
          setErrors(validationErrors)
        }
      }
    }
  )

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const handleArrayChange = (field, index, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }))
  }

  const addArrayItem = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }))
  }

  const removeArrayItem = (field, index) => {
    if (formData[field].length > 1) {
      setFormData(prev => ({
        ...prev,
        [field]: prev[field].filter((_, i) => i !== index)
      }))
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.title.trim()) newErrors.title = 'Title is required'
    if (!formData.description.trim()) newErrors.description = 'Description is required'
    if (formData.description.length < 10) newErrors.description = 'Description must be at least 10 characters'
    if (!formData.category.trim()) newErrors.category = 'Category is required'
    if (!formData.startDate) newErrors.startDate = 'Start date is required'
    if (!formData.endDate) newErrors.endDate = 'End date is required'
    
    if (formData.startDate && formData.endDate) {
      if (new Date(formData.startDate) >= new Date(formData.endDate)) {
        newErrors.endDate = 'End date must be after start date'
      }
    }

    if (formData.maxTeamSize < 1 || formData.maxTeamSize > 10) {
      newErrors.maxTeamSize = 'Max team size must be between 1 and 10'
    }

    // Validate arrays have at least one non-empty item
    if (!formData.prizes.some(prize => prize.trim())) {
      newErrors.prizes = 'At least one prize is required'
    }
    if (!formData.rules.some(rule => rule.trim())) {
      newErrors.rules = 'At least one rule is required'
    }
    if (!formData.evaluationCriteria.some(criteria => criteria.trim())) {
      newErrors.evaluationCriteria = 'At least one evaluation criteria is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      toast.error('Please fix the errors in the form')
      return
    }

    // Clean up the data
    const cleanedData = {
      ...formData,
      prizes: formData.prizes.filter(prize => prize.trim()),
      rules: formData.rules.filter(rule => rule.trim()),
      evaluationCriteria: formData.evaluationCriteria.filter(criteria => criteria.trim()),
      maxTeamSize: parseInt(formData.maxTeamSize)
    }

    createMutation.mutate(cleanedData)
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center">
            <TrophyIcon className="h-8 w-8 text-purple-600 mr-3" />
            <div>
              <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl">
                Create New Challenge
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Create an engaging AI challenge for the community
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white shadow rounded-lg">
          <form onSubmit={handleSubmit} className="space-y-6 p-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
              
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                    Challenge Title *
                  </label>
                  <input
                    type="text"
                    name="title"
                    id="title"
                    className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 ${
                      errors.title ? 'border-red-300' : 'border-gray-300'
                    }`}
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Enter a compelling challenge title"
                  />
                  {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                    Description *
                  </label>
                  <textarea
                    name="description"
                    id="description"
                    rows={4}
                    className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 ${
                      errors.description ? 'border-red-300' : 'border-gray-300'
                    }`}
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Describe the challenge, its objectives, and what participants need to accomplish"
                  />
                  {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
                </div>

                <div>
                  <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                    Category *
                  </label>
                  <input
                    type="text"
                    name="category"
                    id="category"
                    className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 ${
                      errors.category ? 'border-red-300' : 'border-gray-300'
                    }`}
                    value={formData.category}
                    onChange={handleInputChange}
                    placeholder="e.g., Energy Management, AI/ML, IoT"
                  />
                  {errors.category && <p className="mt-1 text-sm text-red-600">{errors.category}</p>}
                </div>

                <div>
                  <label htmlFor="difficulty" className="block text-sm font-medium text-gray-700">
                    Difficulty Level *
                  </label>
                  <select
                    name="difficulty"
                    id="difficulty"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                    value={formData.difficulty}
                    onChange={handleInputChange}
                  >
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Timeline */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Timeline</h3>
              
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="startDate" className="block text-sm font-medium text-gray-700">
                    Start Date *
                  </label>
                  <input
                    type="datetime-local"
                    name="startDate"
                    id="startDate"
                    className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 ${
                      errors.startDate ? 'border-red-300' : 'border-gray-300'
                    }`}
                    value={formData.startDate}
                    onChange={handleInputChange}
                  />
                  {errors.startDate && <p className="mt-1 text-sm text-red-600">{errors.startDate}</p>}
                </div>

                <div>
                  <label htmlFor="endDate" className="block text-sm font-medium text-gray-700">
                    End Date *
                  </label>
                  <input
                    type="datetime-local"
                    name="endDate"
                    id="endDate"
                    className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 ${
                      errors.endDate ? 'border-red-300' : 'border-gray-300'
                    }`}
                    value={formData.endDate}
                    onChange={handleInputChange}
                  />
                  {errors.endDate && <p className="mt-1 text-sm text-red-600">{errors.endDate}</p>}
                </div>
              </div>
            </div>

            {/* Team Settings */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Team Settings</h3>
              
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="maxTeamSize" className="block text-sm font-medium text-gray-700">
                    Maximum Team Size *
                  </label>
                  <input
                    type="number"
                    name="maxTeamSize"
                    id="maxTeamSize"
                    min="1"
                    max="10"
                    className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 ${
                      errors.maxTeamSize ? 'border-red-300' : 'border-gray-300'
                    }`}
                    value={formData.maxTeamSize}
                    onChange={handleInputChange}
                  />
                  {errors.maxTeamSize && <p className="mt-1 text-sm text-red-600">{errors.maxTeamSize}</p>}
                  <p className="mt-1 text-sm text-gray-500">Set to 1 for individual challenges</p>
                </div>
              </div>
            </div>

            {/* Prizes */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Prizes</h3>
              {errors.prizes && <p className="mb-2 text-sm text-red-600">{errors.prizes}</p>}
              
              {formData.prizes.map((prize, index) => (
                <div key={index} className="flex items-center space-x-2 mb-2">
                  <input
                    type="text"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                    value={prize}
                    onChange={(e) => handleArrayChange('prizes', index, e.target.value)}
                    placeholder={`Prize ${index + 1} (e.g., $10,000 for 1st place)`}
                  />
                  {formData.prizes.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeArrayItem('prizes', index)}
                      className="p-2 text-red-600 hover:text-red-800"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => addArrayItem('prizes')}
                className="flex items-center text-sm text-purple-600 hover:text-purple-800"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Add Prize
              </button>
            </div>

            {/* Rules */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Rules</h3>
              {errors.rules && <p className="mb-2 text-sm text-red-600">{errors.rules}</p>}
              
              {formData.rules.map((rule, index) => (
                <div key={index} className="flex items-center space-x-2 mb-2">
                  <input
                    type="text"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                    value={rule}
                    onChange={(e) => handleArrayChange('rules', index, e.target.value)}
                    placeholder={`Rule ${index + 1}`}
                  />
                  {formData.rules.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeArrayItem('rules', index)}
                      className="p-2 text-red-600 hover:text-red-800"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => addArrayItem('rules')}
                className="flex items-center text-sm text-purple-600 hover:text-purple-800"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Add Rule
              </button>
            </div>

            {/* Evaluation Criteria */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Evaluation Criteria</h3>
              {errors.evaluationCriteria && <p className="mb-2 text-sm text-red-600">{errors.evaluationCriteria}</p>}
              
              {formData.evaluationCriteria.map((criteria, index) => (
                <div key={index} className="flex items-center space-x-2 mb-2">
                  <input
                    type="text"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                    value={criteria}
                    onChange={(e) => handleArrayChange('evaluationCriteria', index, e.target.value)}
                    placeholder={`Criteria ${index + 1} (e.g., Innovation and creativity)`}
                  />
                  {formData.evaluationCriteria.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeArrayItem('evaluationCriteria', index)}
                      className="p-2 text-red-600 hover:text-red-800"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => addArrayItem('evaluationCriteria')}
                className="flex items-center text-sm text-purple-600 hover:text-purple-800"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Add Criteria
              </button>
            </div>

            {/* Submit Buttons */}
            <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate('/admin/challenges')}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createMutation.isLoading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
              >
                {createMutation.isLoading ? 'Creating...' : 'Create Challenge'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}